import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea,
  Skeleton,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { categoriesApi, Category } from '../services/api';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Add logging for mobile visits to categories page
  useEffect(() => {
    if (isMobile) {
      console.log('Categories Mobile Visit:', {
        url: window.location.href,
        pathname: window.location.pathname,
        userAgent: navigator.userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        timestamp: new Date().toISOString()
      });
    }
  }, [isMobile]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoriesApi.getAll();
      
      // Log detailed API request information
      console.log('Categories API Request:', {
        url: response.config?.url,
        method: response.config?.method,
        headers: response.config?.headers,
        timestamp: new Date().toISOString()
      });
      
      // Log API response details
      console.log('Categories API Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        timestamp: new Date().toISOString()
      });
      
      // Check if the response has valid data
      if (response && response.data) {
        const categoriesData = response.data.data.categories;
        
        if (Array.isArray(categoriesData)) {
          console.log('Categories Data:', {
            count: categoriesData.length,
            categories: categoriesData.map(c => ({
              id: c.id,
              name: c.name,
              hasDescription: !!c.description
            })),
            timestamp: new Date().toISOString()
          });
          
          if (categoriesData.length === 0) {
            console.warn('No categories found in response');
            setError('No categories found. Please check back later.');
          } else {
            setCategories(categoriesData);
            setError(null);
          }
        } else {
          console.error('Invalid categories data format:', {
            received: typeof categoriesData,
            value: categoriesData,
            timestamp: new Date().toISOString()
          });
          setError('Error loading categories. Data format is incorrect.');
        }
      } else {
        console.error('Invalid API response:', {
          response,
          timestamp: new Date().toISOString()
        });
        setError('Error loading categories. Please try again later.');
      }
    } catch (err: any) {
      console.error('Categories API Error:', {
        error: err,
        message: err.message,
        stack: err.stack,
        timestamp: new Date().toISOString()
      });
      setError('Failed to load categories. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category: Category) => {
    // Log category navigation
    console.log('Category Navigation:', {
      categoryId: category.id,
      categoryName: category.name,
      fromUrl: window.location.href,
      toUrl: `/category/${category.id}`,
      isMobile,
      timestamp: new Date().toISOString()
    });
    
    navigate(`/category/${category.id}`);
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} key={item}>
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Skeleton variant="text" width="60%" height={isMobile ? 32 : 40} />
                  <Skeleton variant="text" width="80%" height={isMobile ? 16 : 20} />
                  <Skeleton variant="text" width="40%" height={isMobile ? 16 : 20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="50vh"
        sx={{
          backgroundColor: theme.palette.error.light,
          borderRadius: theme.shape.borderRadius,
          p: { xs: 2, sm: 4 },
          m: { xs: 1, sm: 2 }
        }}
      >
        <Typography 
          color="error.dark" 
          sx={{ 
            textAlign: 'center',
            fontWeight: 500,
            fontSize: { xs: '1rem', sm: '1.125rem' }
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {categories.map((category) => (
          <Grid item xs={12} sm={6} key={category.id}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                  '& .arrow-icon': {
                    transform: 'translateX(4px)',
                  }
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleCategoryClick(category)}
                sx={{ height: '100%' }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: '100%',
                  p: { xs: 2.5, sm: 3 }
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: { xs: 1.5, sm: 2 }
                  }}>
                    <Typography 
                      variant={isMobile ? "h6" : "h5"}
                      component="h2" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 0,
                        fontSize: {
                          xs: '1.125rem',
                          sm: '1.25rem',
                          md: '1.5rem'
                        }
                      }}
                    >
                      {category.name}
                    </Typography>
                    <IconButton 
                      className="arrow-icon"
                      sx={{ 
                        color: 'primary.main',
                        transition: 'transform 0.3s ease',
                        p: { xs: 0.5, sm: 1 }
                      }}
                    >
                      <ArrowForwardIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                    </IconButton>
                  </Box>
                  {category.description && (
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        flex: 1,
                        fontSize: {
                          xs: '0.875rem',
                          sm: '1rem'
                        }
                      }}
                    >
                      {category.description}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Categories; 